import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './enrichment-curated-feed-history.module.sass';
import { Typography, Tooltip, Table, Space } from 'antd';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ProgressBar from '../../../../shared-components/ProgressBar';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import { RightOutlined } from '@ant-design/icons';
import { formatDate, formatDaysTag } from '../../../../redux/utils/date';
import { InfoCircleFilled } from '@ant-design/icons';
import { STATUS } from '../../../../redux/constants';
import { ResponsiveLine } from '@nivo/line';
import { returnChartColors } from '../../../../redux/utils/chartColors';
import { Link } from 'react-router-dom';

function EnrichmentCuratedFeedHistory({
  newData,
  statusNew,
  type,
  availableFeeds,
  availableFeedsStatus,
}) {
  const { Title, Text, Paragraph } = Typography;
  const history = useHistory();
  const [chartData, setChartData] = useState([]);
  let newEnrichmentData = undefined;

  if (type === 'domain') {
    newEnrichmentData = newData?.response;
  } else if (type === 'ipv4') {
    if (newData?.response?.ip2asn) {
      newEnrichmentData = newData?.response?.ip2asn?.[0];
    }
  }

  useEffect(() => {
    if (newEnrichmentData?.listing_score_feeds_explain) {
      let lineChartData = [];

      newEnrichmentData?.listing_score_feeds_explain.forEach(
        (feedExplain, index) => {
          //assemble chart data
          const listedFirst = feedExplain.listed_first?.toString();
          const listedRecent = feedExplain.listed_recent?.toString();

          lineChartData.push({
            id: feedExplain.feed_short_name,
            data: [
              {
                x:
                  listedFirst?.slice(0, 4) +
                  '-' +
                  listedFirst?.slice(4, 6) +
                  '-' +
                  listedFirst?.slice(6, 8),
                y: index,
              },
              {
                x:
                  listedRecent?.slice(0, 4) +
                  '-' +
                  listedRecent?.slice(4, 6) +
                  '-' +
                  listedRecent?.slice(6, 8),
                y: index,
              },
            ],
          });
        },
      );

      setChartData(lineChartData);
    }
  }, [newEnrichmentData]);

  const getFeedDisplayName = (sluggified) => {
    if (
      sluggified &&
      availableFeedsStatus === STATUS.LOADED &&
      availableFeeds?.length > 0
    ) {
      const feedName = availableFeeds.filter(
        (feed) => feed.code === sluggified,
      )?.[0]?.name;

      return feedName ? feedName : sluggified;
    } else {
      return sluggified;
    }
  };

  const createLinkUrl = (sluggified) => {
    let feedUUID = null;

    if (availableFeeds) {
      feedUUID = availableFeeds.filter((feed) => feed.code === sluggified)?.[0]
        ?.uuid;
      return feedUUID ? `/?feed=${feedUUID}` : null;
    } else {
      return null;
    }
  };

  const columns = [
    {
      title: <ColumnTitle columnName="Name" />,
      dataIndex: 'name',
      key: 'name',
      className: styles.name_column,
      render: (value, params) => {
        if (params?.tooltip) {
          return (
            <>
              {value + ' '}
              <Tooltip placement="top" title={params?.tooltip}>
                <InfoCircleFilled />
              </Tooltip>
            </>
          );
        } else {
          return value;
        }
      },
    },
    {
      title: <ColumnTitle columnName="Value" />,
      dataIndex: 'value',
      key: 'value',
    },
    {
      dataIndex: 'progress',
      key: 'progress',
      width: 100,
    },
    {
      dataIndex: 'expandIcon',
      key: 'expandIcon',
      align: 'right',
    },
  ];

  const customTheme = {
    axis: {
      ticks: {
        text: {
          fill: 'rgba(255, 255, 255, .6)', // X and Y axis label color
        },
      },
    },
    crosshair: {
      line: {
        stroke: 'rgba(255, 255, 255, .6)', // crosshair line color
      },
    },
    dots: {
      text: {
        fill: '#ffffff', // point label color
      },
    },
  };

  function returnTableData({ loading }) {
    return [
      {
        key: 1,
        name: 'Curated Feeds History Score',
        tooltip: (
          <Paragraph className="m-0">
            A score based on the frequency and recency of an observable, within
            trusted feeds
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>{newEnrichmentData?.listing_score}</Text>{' '}
            <Text>score</Text>
          </>
        ),
        progress: loading ? (
          <LoadingSkeleton />
        ) : (
          <ProgressBar
            bgcolor={newEnrichmentData?.listing_score}
            width={'72'}
            completed={newEnrichmentData?.listing_score}
          />
        ),
      },
      {
        key: 2,
        name: 'First Seen',
        tooltip: (
          <Paragraph className="m-0">
            A timestamp of the first time a observable appears in the feed
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {newEnrichmentData?.listing_score_explain?.listed_first
              ? formatDate(
                  newEnrichmentData?.listing_score_explain?.listed_first,
                )
              : '-'}
          </Text>
        ),
        progress: null,
      },
      {
        key: 3,
        name: 'First Seen Ago',
        tooltip: null,
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>
              {typeof newEnrichmentData?.listing_score_explain
                ?.listed_first_ago !== 'undefined' &&
              newEnrichmentData?.listing_score_explain?.listed_first_ago != null
                ? newEnrichmentData?.listing_score_explain?.listed_first_ago
                : '-'}
            </Text>
            <Text>
              {newEnrichmentData?.listing_score_explain?.listed_first_ago
                ? formatDaysTag(
                    newEnrichmentData?.listing_score_explain?.listed_first_ago,
                    true,
                  )
                : null}
            </Text>
          </>
        ),
        progress: null,
      },
      {
        key: 4,
        name: 'Listed Recent',
        tooltip: (
          <Paragraph className="m-0">
            The most recent date where this domain or IP has been listed on at
            least one of the cureated feeds
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {newEnrichmentData?.listing_score_explain?.listed_recent
              ? formatDate(
                  newEnrichmentData?.listing_score_explain?.listed_recent,
                )
              : '-'}
          </Text>
        ),
        progress: null,
      },
      {
        key: 5,
        name: 'Listed Recent Ago',
        tooltip: null,
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>
              {typeof newEnrichmentData?.listing_score_explain
                ?.listed_recent_ago !== 'undefined' &&
              newEnrichmentData?.listing_score_explain?.listed_recent_ago !=
                null
                ? newEnrichmentData?.listing_score_explain?.listed_recent_ago
                : '-'}
            </Text>
            <Text>
              {newEnrichmentData?.listing_score_explain?.listed_recent_ago
                ? formatDaysTag(
                    newEnrichmentData?.listing_score_explain?.listed_recent_ago,
                    true,
                  )
                : null}
            </Text>
          </>
        ),
        progress: null,
      },
      {
        key: 6,
        name: 'Listed Span',
        tooltip: (
          <Paragraph className="m-0">
            The number of days between the first seen date and the most recent
            date where this domain or IP has been listed on at least one of the
            curated feeds
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {newEnrichmentData?.listing_score_explain?.listed_span
              ? newEnrichmentData?.listing_score_explain?.listed_span
              : '0'}
          </Text>
        ),
        progress: null,
      },
      {
        key: 7,
        name: 'Listed All',
        tooltip: (
          <Paragraph className="m-0">
            The number of days where this domain or IP has been listed on at
            least one of the curated feeds
          </Paragraph>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {newEnrichmentData?.listing_score_explain?.listings_all
              ? newEnrichmentData?.listing_score_explain?.listings_all
              : '0'}
          </Text>
        ),
        children: [
          {
            key: 71,
            name: <Text>Listing Last 7 Days</Text>,
            value: (
              <Text strong>
                {newEnrichmentData?.listing_score_explain?.listings_last_7
                  ? newEnrichmentData?.listing_score_explain?.listings_last_7
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 72,
            name: <Text>Listing Last 30 Days</Text>,
            value: (
              <Text strong>
                {newEnrichmentData?.listing_score_explain?.listings_last_30
                  ? newEnrichmentData?.listing_score_explain?.listings_last_30
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 73,
            name: <Text>Listing Last 90 Days</Text>,
            value: (
              <Text strong>
                {newEnrichmentData?.listing_score_explain?.listings_last_90
                  ? newEnrichmentData?.listing_score_explain?.listings_last_90
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 74,
            name: <Text>Listing Last 180 Days</Text>,
            value: (
              <Text strong>
                {newEnrichmentData?.listing_score_explain?.listings_last_180
                  ? newEnrichmentData?.listing_score_explain?.listings_last_180
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 75,
            name: <Text>Listing Last 365 Days</Text>,
            value: (
              <Text strong>
                {newEnrichmentData?.listing_score_explain?.listings_last_365
                  ? newEnrichmentData?.listing_score_explain?.listings_last_365
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
        ],
        progress: null,
        expandIcon: (
          <Text strong>
            <RightOutlined id="7" className={styles.expand_indicator} />
          </Text>
        ),
      },
    ];
  }

  return (
    <div
      id="enrichmentCuratedFeedHistory"
      className={styles.enrichment_curated_feed_history}
    >
      <div className={styles.title}>
        <Title level={4}>Curated Feed History</Title>
        <Text>
          A score based on the frequency and recency of an observable, within
          trusted feeds. <Text strong>Silent Push invention</Text>
        </Text>
      </div>

      <Table
        columns={columns}
        pagination={false}
        expandable={{
          rowExpandable: (record) => record?.children !== undefined,
          expandIcon: ({ expanded, onExpand, record }) => {
            if (expanded && record?.expandIcon) {
              if (document.getElementById(record?.key) && record?.expandIcon) {
                document.getElementById(record?.key).classList.add('expanded');
              }
            } else {
              if (document.getElementById(record?.key) && record?.expandIcon) {
                document
                  .getElementById(record?.key)
                  .classList.remove('expanded');
              }
            }
            return <></>;
          },
          expandRowByClick: true,
        }}
        dataSource={returnTableData({
          loading: !(statusNew === STATUS.LOADED),
        })}
      />

      {chartData.length > 0 && (
        <>
          <div
            id="historyChart"
            className={styles.historyChart}
            style={{ height: `${6 * chartData.length}rem` }}
          >
            <ResponsiveLine
              animate
              axisTop={null}
              axisRight={null}
              axisBottom={{
                format: '%Y-%m-%d',
                tickValues: 'every 3 months',
                tickPadding: 15,
              }}
              axisLeft={{
                tickSize: 1,
                tickPadding: 0,
                tickRotation: 0,
                tickValues: chartData.map((data, index) => index), // Array of integers for tick values
                tickCount: 10,
                format: () => ``, // Customize the label format,
              }}
              xFormat="time:%Y-%m-%d"
              xScale={{
                format: '%Y-%m-%d',
                precision: 'day',
                type: 'time',
                useUTC: true,
              }}
              yScale={{
                type: 'linear',
                min: -0.5,
                max: chartData.length - 0.3,
                reverse: false,
              }}
              curve="stepAfter"
              colors={returnChartColors(
                { r: 60, g: 154, b: 323 },
                { r: 240, g: 98, b: 74 },
                5,
              )}
              data={chartData}
              margin={{ top: 32, right: 32, bottom: 48, left: 32 }}
              lineWidth={20}
              pointSize={1}
              enablePoints={true}
              enablePointLabel
              pointLabel={'xFormatted'}
              pointLabelYOffset={-15}
              enableGridY={false}
              enableGridX={true}
              enableSlices={'y'}
              enableCrosshair={true}
              sliceTooltip={(tooltip) => {
                const feedId = tooltip.slice.points[0].serieId;
                const chartFeedObject = newEnrichmentData?.listing_score_feeds_explain?.filter(
                  (feed) => feedId === feed?.feed_short_name,
                )?.[0];

                return (
                  <div className={styles.chartTooltip}>
                    <Title
                      level={5}
                      style={{ color: tooltip.slice.points?.[0].serieColor }}
                    >
                      {tooltip.slice.points?.[0].serieId}
                    </Title>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>First seen: </Paragraph>
                      <Text strong className="ms-05">
                        {tooltip.slice.points?.[1].data.xFormatted}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Fist seen ago: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listed_first_ago
                          ? chartFeedObject.listed_first_ago
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listed recent: </Paragraph>
                      <Text strong className="ms-05">
                        {tooltip.slice.points?.[0].data.xFormatted}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listed recent ago: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listed_recent_ago
                          ? chartFeedObject.listed_recent_ago
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listed span: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listed_span
                          ? chartFeedObject.listed_span
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing all: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_all
                          ? chartFeedObject.listings_all
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing last 7 days: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_last_7 ||
                        chartFeedObject?.listings_last_7 === 0
                          ? chartFeedObject.listings_last_7
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing last 30 days: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_last_30 ||
                        chartFeedObject?.listings_last_30 === 0
                          ? chartFeedObject.listings_last_30
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing last 90 days: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_last_90 ||
                        chartFeedObject?.listings_last_90 === 0
                          ? chartFeedObject.listings_last_90
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing last 180 days: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_last_180 ||
                        chartFeedObject?.listings_last_180 === 0
                          ? chartFeedObject.listings_last_180
                          : '-'}
                      </Text>
                    </div>
                    <div className={styles.tooltipInfo}>
                      <Paragraph>Listing last 365 days: </Paragraph>
                      <Text strong className="ms-05">
                        {chartFeedObject?.listings_last_365 ||
                        chartFeedObject?.listings_last_365 === 0
                          ? chartFeedObject.listings_last_365
                          : '-'}
                      </Text>
                    </div>
                  </div>
                );
              }}
              theme={customTheme}
            />
          </div>

          <div className={styles.customLegends}>
            {chartData.map((data, index) => {
              const linkUrl = createLinkUrl(data.id);
              const colorArray = returnChartColors(
                { r: 60, g: 154, b: 323 },
                { r: 240, g: 98, b: 74 },
                5,
              );

              if (linkUrl) {
                return (
                  <Link to={linkUrl}>
                    <div
                      className={styles.dot}
                      style={{
                        backgroundColor: colorArray[index % colorArray.length],
                      }}
                    />
                    {getFeedDisplayName(data.id)}
                  </Link>
                );
              } else {
                return (
                  <>
                    <Text className={styles.invalidLink}>
                      <div
                        className={styles.dot}
                        style={{
                          backgroundColor:
                            colorArray[index % colorArray.length],
                        }}
                      />
                      {getFeedDisplayName(data.id)}
                    </Text>
                  </>
                );
              }
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default EnrichmentCuratedFeedHistory;
